import { type ProcessedProductMedia } from "./processedProductMedia";
import { loadProductSheetImages, productSheetFilename, type ProductSheetData, type SheetImages } from "./processedProductSheet";
import { productZip } from "./productZip";

export type ProductExportFormat = "pdf" | "zip";
export interface ProductArchiveImage { url: string; path: string; label: string }
type SharedMedia = { icon?: string; logo?: string };
const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
const gallery = (value: unknown): string[] => Array.isArray(value) ? value.map(text) : [];

export function freshProductArchiveImages(product: SharedMedia & { url?: string; urlArr?: string[] }): ProductArchiveImage[] {
  return [
    { url: text(product.icon), path: "ikon", label: "Ikon" },
    { url: text(product.logo), path: "logotyp", label: "Logotyp" },
    { url: text(product.url), path: "huvudbild", label: "Huvudbild" },
    ...gallery(product.urlArr).map((url, index) => ({ url, path: `extrabild-${String(index + 1).padStart(2, "0")}`, label: `Extrabild ${index + 1}` })),
  ].filter((image) => !!image.url);
}

export function processedProductArchiveImages(product: SharedMedia & ProcessedProductMedia): ProductArchiveImage[] {
  return [
    { url: text(product.icon), path: "ikon", label: "Ikon" },
    { url: text(product.logo), path: "logotyp", label: "Logotyp" },
    ...(["sv", "en"] as const).flatMap((language) => [
      { url: text(product[`mainImage_${language}`]), path: `huvudbild-${language}`, label: `${language === "sv" ? "Svensk" : "Engelsk"} huvudbild` },
      ...gallery(product[`imageGallery_${language}`]).map((url, index) => ({ url, path: `extrabild-${language}-${String(index + 1).padStart(2, "0")}`, label: `${language === "sv" ? "Svensk" : "Engelsk"} extrabild ${index + 1}` })),
    ]),
    { url: text(product.mainImage), path: "huvudbild-aldre", label: "Äldre gemensam huvudbild" },
    ...gallery(product.imageGallery).map((url, index) => ({ url, path: `extrabild-aldre-${String(index + 1).padStart(2, "0")}`, label: `Äldre extrabild ${index + 1}` })),
  ].filter((image) => !!image.url);
}

export class ProductArchiveError extends Error {}
const MAX_IMAGE_BYTES = 20_000_000;
const MAX_ARCHIVE_IMAGES_BYTES = 150_000_000;
const imageFormat = (bytes: Uint8Array, contentType: string): { extension: string; mime: string; pdf: boolean } | null => {
  if (bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71) return { extension: "png", mime: "image/png", pdf: true };
  if (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return { extension: "jpg", mime: "image/jpeg", pdf: true };
  if (String.fromCharCode(...Array.from(bytes.subarray(0, 6))).startsWith("GIF8")) return { extension: "gif", mime: "image/gif", pdf: false };
  if (String.fromCharCode(...Array.from(bytes.subarray(0, 4))) === "RIFF" && String.fromCharCode(...Array.from(bytes.subarray(8, 12))) === "WEBP") return { extension: "webp", mime: "image/webp", pdf: false };
  // Preserve other uploaded image formats as originals, without asking the PDF renderer to decode them.
  const formats: Record<string, string> = { "image/svg+xml": "svg", "image/avif": "avif", "image/heic": "heic", "image/heif": "heif", "image/tiff": "tiff", "image/bmp": "bmp" };
  const mime = contentType.split(";")[0].trim().toLowerCase();
  return formats[mime] && bytes.length ? { extension: formats[mime], mime, pdf: false } : null;
};
const dataUrl = (bytes: Uint8Array, mime: string) => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + 8192)));
  return `data:${mime};base64,${btoa(binary)}`;
};

export async function createProductArchive(data: ProductSheetData, images: ProductArchiveImage[], renderPdf: (data: ProductSheetData, images: SheetImages) => Promise<Blob>): Promise<{ blob: Blob; missingPdfImages: number }> {
  const urls = Array.from(new Set(images.map((image) => image.url)));
  const pdfImageUrls = new Set([data.mainImage, ...data.gallery.slice(0, 5)]);
  const originals = new Map<string, { bytes: Uint8Array; extension: string; preview: string | null }>();
  let next = 0, total = 0, sizeLimitExceeded = false;
  const failures = new Set<string>();
  const download = async () => {
    while (next < urls.length) {
      const url = urls[next++];
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: "omit", referrerPolicy: "no-referrer" });
        if (!response.ok) throw new Error("Image unavailable");
        if (Number(response.headers.get("content-length")) > MAX_IMAGE_BYTES) { sizeLimitExceeded = true; throw new Error("Image too large"); }
        const bytes = new Uint8Array(await response.arrayBuffer());
        total += bytes.length;
        const format = imageFormat(bytes, response.headers.get("content-type") || "");
        if (bytes.length > MAX_IMAGE_BYTES || total > MAX_ARCHIVE_IMAGES_BYTES) { sizeLimitExceeded = true; throw new Error("Image too large"); }
        if (!format) throw new Error("Image unavailable");
        originals.set(url, { bytes, extension: format.extension, preview: format.pdf && pdfImageUrls.has(url) ? dataUrl(bytes, format.mime) : null });
      } catch { failures.add(url); }
      finally { clearTimeout(timer); }
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, urls.length) }, download));
  if (failures.size) {
    const labels = images.filter((image) => failures.has(image.url)).map((image) => image.label);
    throw new ProductArchiveError(`Alla bilder kunde inte hämtas: ${labels.slice(0, 4).join(", ")}${labels.length > 4 ? ` och ${labels.length - 4} till` : ""}. Försök igen. Kontrollera att bilderna går att öppna i produktens bildavsnitt.${sizeLimitExceeded ? " En bild får vara högst 20 MB och bilderna totalt högst 150 MB." : ""}`);
  }
  if (images.reduce((sum, image) => sum + originals.get(image.url)!.bytes.length, 0) > MAX_ARCHIVE_IMAGES_BYTES) throw new ProductArchiveError("Bilderna i ZIP-filen är för stora. Bildfilerna får tillsammans vara högst 150 MB.");
  const previews = new Map<string, string | null>();
  originals.forEach((image, url) => { if (pdfImageUrls.has(url)) previews.set(url, image.preview); });
  const pdfImages = await loadProductSheetImages(data, previews);
  const sheet = await renderPdf(data, pdfImages);
  const files = [{ name: productSheetFilename(data), bytes: new Uint8Array(await sheet.arrayBuffer()) },
    ...images.map((image) => { const original = originals.get(image.url)!; return { name: `${image.path}.${original.extension}`, bytes: original.bytes }; })];
  return { blob: productZip(files), missingPdfImages: pdfImages.missing };
}

export const productArchiveFilename = (data: Pick<ProductSheetData, "name" | "language">) => productSheetFilename(data).replace(/\.pdf$/, ".zip");
